// Hooks for the sessions surface. All keyed off currentProfile so profile
// switching invalidates fetched data automatically.

import { useCallback, useEffect, useState } from 'react'
import { usePatient } from '../context/PatientContext'
import * as sessions from '../services/sessionsService'

export function useSessionsList(status) {
  const { currentProfile } = usePatient()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const refresh = useCallback(async () => {
    if (!currentProfile) {
      setData(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const page = await sessions.listSessions(currentProfile, status)
      setData(page)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [currentProfile, status])

  useEffect(() => {
    setData(null)
    refresh()
  }, [refresh])

  return { data, loading, error, refresh }
}

export function useSession(eventId) {
  const { currentProfile } = usePatient()
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const refresh = useCallback(async () => {
    if (!currentProfile || !eventId) {
      setDetail(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const d = await sessions.getSession(currentProfile, eventId)
      setDetail(d)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [currentProfile, eventId])

  useEffect(() => {
    setDetail(null)
    refresh()
  }, [refresh])

  const reschedule = useCallback(
    async (body) => {
      if (!currentProfile) return null
      const r = await sessions.reschedule(currentProfile, eventId, body)
      await refresh()
      return r
    },
    [currentProfile, eventId, refresh],
  )

  const cancel = useCallback(
    async (body) => {
      if (!currentProfile) return null
      const r = await sessions.cancel(currentProfile, eventId, body)
      await refresh()
      return r
    },
    [currentProfile, eventId, refresh],
  )

  const submitFeedback = useCallback(
    async (body) => {
      if (!currentProfile) return null
      const fb = await sessions.submitFeedback(currentProfile, eventId, body)
      await refresh()
      return fb
    },
    [currentProfile, eventId, refresh],
  )

  return { detail, loading, error, refresh, reschedule, cancel, submitFeedback }
}
